import React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    Grid,
    IconButton,
    TextField,
    Tabs,
    Tab,
    Chip,
    Paper,
    Stack,
    Skeleton,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { GetServerSideProps } from "next";
import config from "../../../../../config";
import { EcommerceEventList } from "../../../../../models/Event";
import PageWrapper from "../../../../../components/PageWrapper";
import Typography from "@mui/material/Typography";
import {
    getErrorMessage,
    numberFormatter,
    priceFormatter,
    setupStyleMapFromStyleMap,
} from "../../../../../utils/helpers";
import toast from "../../../../../utils/toast";
import { useRouter } from "next/router";
import SpecialDepartmentEndpoints from "../../../../../api/SpecialDepartmentEndpoints";
import {
    CorporateCustomer,
    PortalSalesResponse,
    SeasonSummary,
} from "../../../../../models/CorporateApplication";
import DownloadIcon from "@mui/icons-material/Download";
import CustomerDetailsForm from "../../../../../components/corporate/CustomerDetailsForm";
import Head from "next/head";
import NothingHereYet from "../../../../../components/NothingHereYet";
import CopyIcon from "@mui/icons-material/ContentCopy";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`season-tabpanel-${index}`}
            aria-labelledby={`season-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `season-tab-${index}`,
        "aria-controls": `season-tabpanel-${index}`,
    };
}

type Props = {
    eventList: EcommerceEventList;
    sdaCode: string;
};

function stateToRequest(
    customer: CorporateCustomer,
    countryId: number | null,
    stateId: number | null,
) {
    return {
        requests: [
            {
                field: "corporationname",
                value: customer.corporationName,
            },
            {
                field: "customeraddress",
                value: customer.address,
            },
            {
                field: "customercity",
                value: customer.city,
            },
            {
                field: "customerpostal",
                value: customer.postalCode,
            },
            {
                field: "customerstateid",
                value: stateId ? "" + stateId : null,
            },
            {
                field: "customercountryid",
                value: countryId ? "" + countryId : null,
            },
            {
                field: "customerfax",
                value: customer.fax,
            },
            {
                field: "contactfirstname",
                value: customer.contact.firstName,
            },
            {
                field: "contactlastname",
                value: customer.contact.lastName,
            },
            {
                field: "contactemail",
                value: customer.contact.email,
            },
            {
                field: "contacttitle",
                value: customer.contact.title,
            },
            {
                field: "contactdepartment",
                value: customer.contact.department,
            },
            {
                field: "contactphone",
                value: customer.contact.phone,
            },
        ],
    };
}

// Component for displaying sales summary cards
function SalesSummaryCard({ summary }: { summary: any }) {
    const theme = useTheme();

    return (
        <Card
            variant="outlined"
            sx={{
                height: "100%",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: theme.shadows[4],
                    borderColor: theme.palette.primary.main,
                },
            }}
        >
            <CardContent>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <Box>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            gutterBottom
                        >
                            {summary.name}
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                            {numberFormatter(summary.quantity)} Sold
                        </Typography>
                    </Box>
                    <Box textAlign="right">
                        <Chip
                            label={priceFormatter(summary.total)}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

// Component for season overview stats
function SeasonStats({
    totalSales,
    totalQuantity,
}: {
    totalSales: number;
    totalQuantity: number;
}) {
    const theme = useTheme();

    return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        background: alpha(theme.palette.primary.main, 0.08),
                        borderRadius: 2,
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <AttachMoneyIcon
                            color="primary"
                            sx={{ fontSize: 40 }}
                        />
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Total Revenue
                            </Typography>
                            <Typography
                                variant="h4"
                                fontWeight={700}
                                color="primary"
                            >
                                {priceFormatter(totalSales)}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        background: alpha(theme.palette.success.main, 0.08),
                        borderRadius: 2,
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <ShoppingCartIcon
                            color="success"
                            sx={{ fontSize: 40 }}
                        />
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Total Tickets Sold
                            </Typography>
                            <Typography
                                variant="h4"
                                fontWeight={700}
                                color="success.main"
                            >
                                {totalQuantity.toLocaleString()}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            </Grid>
        </Grid>
    );
}

export default function CorporateLogin({ eventList, sdaCode }: Props) {
    const theme = useTheme();
    const [password, setPassword] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [portal, setPortal] = React.useState<PortalSalesResponse | undefined>(
        undefined,
    );
    const [countryId, setCountryId] = React.useState<number | null>(null);
    const [stateId, setStateId] = React.useState<number | null>(null);
    const [tabValue, setTabValue] = React.useState(0);

    function onChangePassword(e: React.ChangeEvent<HTMLInputElement>) {
        setPassword(e.target.value);
    }

    async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        setLoading(true);

        try {
            const sda = await SpecialDepartmentEndpoints.getApplication(
                sdaCode,
                password,
            );
            setPortal(sda);
            setCountryId(sda.customer.country?.id ?? null);
            setStateId(sda.customer.country?.id ?? null);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }

    async function onSubmitCustomerDetails(
        e: React.FormEvent<HTMLFormElement>,
    ) {
        e.preventDefault();

        if (!portal) return;

        setLoading(true);

        try {
            await SpecialDepartmentEndpoints.editCustomer(
                sdaCode,
                password,
                stateToRequest(portal.customer, countryId, stateId),
            );
            toast.success("Customer details updated!");
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }

    async function onDownloadFlyer() {
        setLoading(true);

        try {
            const flyer = await SpecialDepartmentEndpoints.getFlyer(
                sdaCode,
                password,
            );
            const url = window.URL.createObjectURL(new Blob([flyer]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `flyer-${portal?.customer.id}.pdf`);
            document.body.appendChild(link);
            link.click();

            toast.success("Flyer downloaded!");

            link.parentNode?.removeChild(link);
        } catch (err) {
            toast.error("An error occurred while downloading flyer.");
        } finally {
            setLoading(false);
        }
    }

    function onCustomerFieldChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (!portal) return;
        setPortal({
            ...portal,
            customer: {
                ...portal.customer,
                [e.target.name]: e.target.value,
            },
        });
    }

    function onContactFieldChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (!portal) return;
        setPortal({
            ...portal,
            customer: {
                ...portal.customer,
                contact: {
                    ...portal.customer.contact,
                    [e.target.name]: e.target.value,
                },
            },
        });
    }

    function onCountryChange(e: React.ChangeEvent<any>) {
        setCountryId(parseInt(e.target.value));
        setStateId(null);
    }

    function onStateChange(e: React.ChangeEvent<any>) {
        setStateId(parseInt(e.target.value));
    }

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    React.useEffect(() => {
        if (eventList.newStyleMap) {
            setupStyleMapFromStyleMap(eventList.newStyleMap);
        }
    }, []);

    const pageTitle = `Your ${eventList.clientName} Dashboard | AuthentiGATE`;

    // Create all seasons array (current + historical)
    const allSeasons = React.useMemo(() => {
        if (!portal) return [];

        const currentSeason = {
            seasonId: 0,
            seasonName: portal.application.landingPage.name,
            salesSummaries: portal.summaries,
            totalSales: portal.summaries.reduce(
                (sum: number, s: any) => sum + s.total,
                0,
            ),
            totalQuantity: portal.summaries.reduce(
                (sum: number, s: any) => sum + s.quantity,
                0,
            ),
        };

        return [currentSeason, ...(portal.historicalSeasons || [])];
    }, [portal]);

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="robots" content="noindex" />
            </Head>
            <PageWrapper eventList={eventList} noCart>
                <Container maxWidth="lg">
                    {!portal && (
                        <Box sx={{ maxWidth: 600, mx: "auto" }}>
                            {eventList.clientId === 70 && (
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    If you forgot your password, please contact{" "}
                                    <a
                                        href={`mailto:admissions@theex.com?subject=Corporate Password Reset for ${eventList.partnerName}&body=Please reset my Admin password.`}
                                    >
                                        admissions@theex.com
                                    </a>
                                </Alert>
                            )}
                            <form onSubmit={onSubmit}>
                                <Card elevation={3}>
                                    <CardContent sx={{ p: 4 }}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="h4"
                                                    color="primaryText.main"
                                                    textAlign="center"
                                                    gutterBottom
                                                >
                                                    Corporate Portal Access
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    textAlign="center"
                                                    paragraph
                                                >
                                                    Please enter your admin
                                                    password to access your
                                                    dashboard
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    label="Admin Password"
                                                    placeholder="Enter your password"
                                                    value={password}
                                                    onChange={onChangePassword}
                                                    disabled={loading}
                                                    type="password"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    type="submit"
                                                    fullWidth
                                                    disabled={
                                                        loading || !password
                                                    }
                                                    variant="contained"
                                                    size="large"
                                                    sx={{ py: 1.5 }}
                                                >
                                                    Access Dashboard
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </form>
                        </Box>
                    )}

                    {portal && (
                        <Grid container spacing={3}>
                            {/* Header Section */}
                            <Grid item xs={12}>
                                <Card elevation={3}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Stack spacing={3}>
                                            <Box textAlign="center">
                                                <Typography
                                                    variant="h4"
                                                    color="primaryText.main"
                                                    gutterBottom
                                                >
                                                    {eventList.partnerName}{" "}
                                                    Dashboard
                                                </Typography>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    justifyContent="center"
                                                    alignItems="center"
                                                >
                                                    <Typography
                                                        variant="body1"
                                                        color="text.secondary"
                                                    >
                                                        Access Code:
                                                    </Typography>
                                                    <Chip
                                                        label={
                                                            portal.application
                                                                .code
                                                        }
                                                        color="primary"
                                                        variant="outlined"
                                                        onDelete={() => {
                                                            if (
                                                                navigator.clipboard
                                                            ) {
                                                                navigator.clipboard.writeText(
                                                                    portal
                                                                        .application
                                                                        .url ??
                                                                        "",
                                                                );
                                                                toast.success(
                                                                    "Portal link copied!",
                                                                );
                                                            }
                                                        }}
                                                        deleteIcon={
                                                            <CopyIcon />
                                                        }
                                                    />
                                                </Stack>
                                            </Box>

                                            <Stack
                                                direction={{
                                                    xs: "column",
                                                    sm: "row",
                                                }}
                                                spacing={2}
                                                justifyContent="center"
                                            >
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={onDownloadFlyer}
                                                    startIcon={<DownloadIcon />}
                                                >
                                                    Download Personalized Flyer
                                                </Button>
                                                {eventList.clientId === 70 && (
                                                    <>
                                                        <Button
                                                            variant="contained"
                                                            color="warning"
                                                            href="https://cdn.authentigate.ca/clients/static/cne/2026_CNE_CORPORATE_EFLYER.pdf"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            startIcon={
                                                                <DownloadIcon />
                                                            }
                                                        >
                                                            Download E-Flyer
                                                        </Button>
                                                        {/* <Button
                                                            variant="contained"
                                                            color="info"
                                                            href="https://cdn.authentigate.ca/clients/static/cne/2026_CNE_CORPORATE_POSTER.pdf"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            startIcon={
                                                                <DownloadIcon />
                                                            }
                                                        >
                                                            Download Poster
                                                        </Button> */}
                                                    </>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Sales Summary Section */}
                            <Grid item xs={12}>
                                <Card elevation={3}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography
                                            variant="h5"
                                            color="primaryText.main"
                                            gutterBottom
                                        >
                                            Sales Analytics
                                        </Typography>

                                        <Box
                                            sx={{
                                                borderBottom: 1,
                                                borderColor: "divider",
                                            }}
                                        >
                                            <Tabs
                                                value={tabValue}
                                                onChange={handleTabChange}
                                                variant="scrollable"
                                                scrollButtons="auto"
                                                sx={{
                                                    "& .MuiTab-root": {
                                                        minHeight: 48,
                                                        textTransform: "none",
                                                    },
                                                }}
                                            >
                                                {allSeasons.map(
                                                    (season, index) => (
                                                        <Tab
                                                            key={
                                                                season.seasonId
                                                            }
                                                            label={
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    alignItems="center"
                                                                >
                                                                    <CalendarTodayIcon fontSize="small" />
                                                                    <span>
                                                                        {
                                                                            season.seasonName
                                                                        }
                                                                    </span>
                                                                    {index ===
                                                                        0 && (
                                                                        <Chip
                                                                            label="Active"
                                                                            size="small"
                                                                            color="success"
                                                                        />
                                                                    )}
                                                                </Stack>
                                                            }
                                                            {...a11yProps(
                                                                index,
                                                            )}
                                                        />
                                                    ),
                                                )}
                                            </Tabs>
                                        </Box>

                                        {allSeasons.map((season, index) => (
                                            <TabPanel
                                                key={season.seasonId}
                                                value={tabValue}
                                                index={index}
                                            >
                                                <SeasonStats
                                                    totalSales={
                                                        season.totalSales
                                                    }
                                                    totalQuantity={
                                                        season.totalQuantity
                                                    }
                                                />

                                                <Grid container spacing={2}>
                                                    {season.salesSummaries.map(
                                                        (
                                                            summary: any,
                                                            idx: number,
                                                        ) => (
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                sm={6}
                                                                md={4}
                                                                key={idx}
                                                            >
                                                                <SalesSummaryCard
                                                                    summary={
                                                                        summary
                                                                    }
                                                                />
                                                            </Grid>
                                                        ),
                                                    )}
                                                    {season.salesSummaries
                                                        .length === 0 && (
                                                        <Grid item xs={12}>
                                                            <NothingHereYet message="No sales recorded for this season." />
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </TabPanel>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Customer Details Section */}
                            <Grid item xs={12}>
                                <form onSubmit={onSubmitCustomerDetails}>
                                    <Card elevation={3}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography
                                                variant="h5"
                                                color="primaryText.main"
                                                gutterBottom
                                                sx={{ mb: 3 }}
                                            >
                                                Contact Information
                                            </Typography>

                                            <CustomerDetailsForm
                                                customer={portal.customer}
                                                countryId={countryId}
                                                stateId={stateId}
                                                onCustomerFieldChange={
                                                    onCustomerFieldChange
                                                }
                                                onContactFieldChange={
                                                    onContactFieldChange
                                                }
                                                onCountryChange={
                                                    onCountryChange
                                                }
                                                onStateChange={onStateChange}
                                            />

                                            <Box sx={{ mt: 3 }}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    fullWidth
                                                    disabled={loading}
                                                    size="large"
                                                >
                                                    Save Changes
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </form>
                            </Grid>
                        </Grid>
                    )}
                </Container>
            </PageWrapper>
        </>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const cartUrl = context.params?.cartUrl;
    const sdaCode = context.params?.sdaCode;
    let data: any,
        error = false;

    try {
        const res = await fetch(
            `${config.BACKEND_URI}/sales/special-events/${sdaCode}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-Sales-Context": "ECOMMERCE",
                    Cookie: context.req.headers.cookie ?? "",
                },
                credentials: "include",
            },
        );
        if (res.status !== 200) {
            error = true;
            console.log(res.status, res.statusText);
            console.log(await res.text());
        } else {
            data = await res.json();
        }
    } catch (err) {
        console.log(err);
        error = true;
    }

    if (data?.newStyleMap) {
        setupStyleMapFromStyleMap(data.newStyleMap);
    }

    return {
        props: {
            eventList: data,
            sdaCode,
        },
        notFound: error,
    };
};
